import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, Link} from "react-router-dom";
import {toast} from "react-toastify";
import {confirmForgotPassword, retryOtpForgotPassword} from "../../../services/auth";
import {useTranslation} from "react-i18next";
import styles from "./ForgotPasswordConfirmPage.module.css";

const ForgotPasswordConfirmPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [otp, setOtp] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [error, setError] = useState("");

    // location.state is lost on page reload, so fall back to sessionStorage
    // (set in ForgotPasswordPage on successful email submit). This is what makes
    // "resend OTP after a while" survive a refresh.
    const verificationToken =
        location.state?.verificationToken ||
        sessionStorage.getItem("gredus.forgotPasswordToken");

    useEffect(() => {
        if (!verificationToken) {
            navigate("/forgot-password", {replace: true});
        }
    }, [verificationToken, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            setError(t("auth.otpMustBe6Digits"));
            return;
        }

        try {
            setIsSubmitting(true);
            setError("");
            await confirmForgotPassword(verificationToken, parseInt(otp));
            navigate("/forgot-password/set-new-password", {
                state: {verificationToken: verificationToken}
            });
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err.message ||
                t("auth.otpVerifyError");
            setError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendOtp = async (e) => {
        e.preventDefault();
        try {
            setIsRetrying(true);
            setError("");
            await retryOtpForgotPassword(verificationToken);
            toast.success(t("toasts.newOtpSent"));
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err.message ||
                t("auth.otpResendError");
            setError(msg);
        } finally {
            setIsRetrying(false);
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.formContainer}>
                    <Link to="/">
                        <img src="/gredus-logo.svg" alt="logo" className={styles.logo}/>
                    </Link>
                    <h2 className={styles.title}>{t("auth.otpTitle")}</h2>
                    <p className={styles.description}>
                        {t("auth.otpForgotDescription")}
                    </p>

                    <form onSubmit={handleSubmit} className={styles.form}>
                        {error && <p className={styles.error}>{error}</p>}

                        <div className={styles.formGroup}>
                            <input
                                type="text"
                                maxLength="6"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                className={styles.input}
                                placeholder="123456"
                                autoFocus
                            />
                        </div>

                        <button type="submit" disabled={isSubmitting || otp.length !== 6} className={styles.submitBtn}>
                            {isSubmitting ? t("auth.otpVerifying") : t("auth.otpConfirm")}
                        </button>
                    </form>

                    <p className={styles.backText}>
                        {t("auth.otpResendPrompt")}{" "}
                        <span onClick={handleResendOtp}
                              className={`${styles.resendLink} ${isRetrying ? styles.disabled : ""}`}>
                            {isRetrying ? t("auth.otpResendSending") : t("auth.otpResendLink")}
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordConfirmPage;
